import { useState } from 'react';
import { Modal } from 'antd';
import type { IQuickServiceItem } from 'QuickBooking/signal';
import serviceAssets from 'QuickBooking/service-images';
import shopSelectors from 'services/selectors/shop';
import { IQuickBookingServiceItem } from 'services/types/quickBooking';
import { ModalBody, HeaderBar, HeaderTitle, HeaderClose, FooterBar } from './booking-history-styles';
import { Grid, ServiceCard, CardImg, CardName, TabBar, TabButton, EmptyState } from './add-services-styles';

type Props = {
  open: boolean;
  services: IQuickBookingServiceItem[];
  selected: IQuickServiceItem[]; // services already in the composer
  onClose: () => void;
  onToggle: (svc: IQuickBookingServiceItem) => void;
};

type Tab = 'quick' | 'full';

// A normalized card the grid renders regardless of the source list.
type ServiceCardModel = { key: string; image?: string; svc: IQuickBookingServiceItem };

// Grid of services (image + name); click toggles a service in the composer.
// Two tabs: "Quick services" (the composer's curated list) and "Full services"
// (the whole shop catalog from shopSelectors.allServices).
const AddServicesModal = ({ open, services, selected, onClose, onToggle }: Props) => {
  const [tab, setTab] = useState<Tab>('quick');
  const allServices = shopSelectors.allServices();
  const selectedNames = selected.map((o) => String(o.label));

  // Quick services carry only id/name/price → resolve the image by name from local assets.
  const quickCards: ServiceCardModel[] = services.map((svc) => ({
    key: `q-${svc.id}`,
    image: serviceAssets[svc.serviceName],
    svc,
  }));

  // Full catalog services carry their own image URL; map to the toggle contract shape.
  const fullCards: ServiceCardModel[] = allServices.map((svc) => ({
    key: `f-${svc.id}`,
    image: svc.image || serviceAssets[svc.name],
    svc: { id: Number(svc.id) || 0, serviceName: svc.name, price: svc.price },
  }));

  const cards = tab === 'quick' ? quickCards : fullCards;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      closable={false}
      closeIcon={null}
      width={980}
      centered
      styles={{ content: { padding: 0, borderRadius: 14, overflow: 'hidden' }, body: { padding: 0 } }}
    >
      <ModalBody>
        <HeaderBar>
          <HeaderTitle>SELECT SERVICES</HeaderTitle>
          <HeaderClose type="button" onClick={onClose}>
            Close
          </HeaderClose>
        </HeaderBar>

        <TabBar>
          <TabButton type="button" $active={tab === 'quick'} onClick={() => setTab('quick')}>
            Quick services
          </TabButton>
          <TabButton type="button" $active={tab === 'full'} onClick={() => setTab('full')}>
            Full services
          </TabButton>
        </TabBar>

        {cards.length > 0 ? (
          <Grid>
            {cards.map(({ key, image, svc }) => {
              const isSelected = selectedNames.includes(svc.serviceName);
              return (
                <ServiceCard
                  key={key}
                  type="button"
                  $selected={isSelected}
                  onClick={() => onToggle(svc)}
                >
                  <CardImg>{image && <img src={image} alt="" />}</CardImg>
                  <CardName $selected={isSelected}>{svc.serviceName}</CardName>
                </ServiceCard>
              );
            })}
          </Grid>
        ) : (
          <EmptyState>No services available</EmptyState>
        )}

        <FooterBar type="button" onClick={onClose}>
          Done
        </FooterBar>
      </ModalBody>
    </Modal>
  );
};

export default AddServicesModal;
